/**
 * The small form fields the Scripts editors share: a number in a display
 * unit (stored SI, invariant 5), a text field, and a select. Each commits on
 * blur or Enter, so a half-typed value is never saved.
 *
 * Inside a `FieldTable` each field is a row of a plain table, as the
 * parameter inspector reads: what it is, the value, and its unit, each in its
 * own column. Outside one (a floating editor on the drawing) a field is a
 * label over its control.
 */
import { createContext, useContext, useEffect, useId, useState, type ReactNode } from "react";
import { loadFolds, saveFold } from "./preferences";
import { typedNumber } from "./units";

const InTable = createContext(false);

/**
 * A plain table of fields: variable, value, unit. Its children are fields, or
 * `FieldRow`s for anything else (a checkbox, a pair of selects, a note).
 */
export function FieldTable({ label, children }: { label?: string; children: ReactNode }) {
  return (
    <table className="ws-params sx-fields" aria-label={label}>
      <tbody>
        <InTable.Provider value={true}>{children}</InTable.Provider>
      </tbody>
    </table>
  );
}

/** One row of a `FieldTable`: the variable, the value (any control), the unit or an action. */
export function FieldRow({
  label,
  htmlFor,
  unit,
  children,
  span = false,
}: {
  label: ReactNode;
  /** The control the variable names, so the name is its label. */
  htmlFor?: string;
  unit?: ReactNode;
  children?: ReactNode;
  /** The value takes the whole row: a note, a list, a button. */
  span?: boolean;
}) {
  if (span) {
    return (
      <tr className="sx-field-span">
        <td colSpan={3}>{children ?? label}</td>
      </tr>
    );
  }
  return (
    <tr>
      <th scope="row">{htmlFor ? <label htmlFor={htmlFor}>{label}</label> : label}</th>
      <td className="ws-param-value">{children}</td>
      <td className="ws-unit">{unit}</td>
    </tr>
  );
}

/** A number field that commits a parsed value, in a display unit, on blur or Enter. */
export function NumberField({
  label,
  value,
  factor = 1,
  unit,
  onChange,
  min,
  placeholder,
}: {
  label: string;
  value: number | null | undefined;
  factor?: number;
  unit?: string;
  onChange: (value: number | null) => void;
  min?: number;
  /** What blank means: "end when reached", "unchanged". */
  placeholder?: string;
}) {
  const id = useId();
  const table = useContext(InTable);
  const shown = value === null || value === undefined ? "" : String(Number((value / factor).toPrecision(6)));
  const [text, setText] = useState(shown);
  useEffect(() => setText(shown), [shown]);
  const commit = () => {
    if (text.trim() === "") return onChange(null);
    const parsed = typedNumber(text);
    if (!Number.isFinite(parsed) || (min !== undefined && parsed < min)) {
      setText(shown);
      return;
    }
    onChange(parsed * factor);
  };
  const input = (
    <input
      id={id}
      className={table ? "ws-param-input" : "sx-number"}
      inputMode="decimal"
      value={text}
      placeholder={placeholder}
      onChange={(event) => setText(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => event.key === "Enter" && commit()}
    />
  );
  if (table) {
    return (
      <FieldRow label={label} htmlFor={id} unit={unit}>
        {input}
      </FieldRow>
    );
  }
  return (
    <label className="sx-field" htmlFor={id}>
      <span>{label}</span>
      <span className="sx-inline">
        {input}
        {unit && <span className="ws-muted ws-small">{unit}</span>}
      </span>
    </label>
  );
}

export function TextField({ label, value, onChange, area = false }: { label: string; value: string; onChange: (value: string) => void; area?: boolean }) {
  const id = useId();
  const table = useContext(InTable);
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  const commit = () => text !== value && onChange(text);
  const control = area ? (
    <textarea id={id} rows={2} value={text} onChange={(event) => setText(event.target.value)} onBlur={commit} />
  ) : (
    <input id={id} value={text} onChange={(event) => setText(event.target.value)} onBlur={commit} onKeyDown={(event) => event.key === "Enter" && commit()} />
  );
  if (table) {
    return (
      <FieldRow label={label} htmlFor={id}>
        {control}
      </FieldRow>
    );
  }
  return (
    <label className="sx-field" htmlFor={id}>
      <span>{label}</span>
      {control}
    </label>
  );
}

export function Select<T extends string>({
  label,
  value,
  options,
  onChange,
  hideLabel = false,
  unit,
}: {
  label: string;
  value: T | "";
  /** With a `group`, options are listed under their group's heading, in the order groups first appear. */
  options: readonly { value: T; label: string; group?: string }[];
  onChange: (value: T) => void;
  hideLabel?: boolean;
  /** In a table, what goes in the unit column: a remove button, say. */
  unit?: ReactNode;
}) {
  const id = useId();
  const table = useContext(InTable);
  const missing = value !== "" && !options.some((option) => option.value === value);
  const control = (
    <select id={id} value={value} onChange={(event) => onChange(event.target.value as T)}>
      {value === "" && <option value="">Choose…</option>}
      {missing && <option value={value}>{value} (not in the design)</option>}
      {[...new Set(options.map((option) => option.group))].map((group) => {
        const members = options
          .filter((option) => option.group === group)
          .map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ));
        return group ? (
          <optgroup key={group} label={group}>
            {members}
          </optgroup>
        ) : (
          members
        );
      })}
    </select>
  );
  if (table) {
    return (
      <FieldRow label={label} htmlFor={id} unit={unit}>
        {control}
      </FieldRow>
    );
  }
  return (
    <label className="sx-field" htmlFor={id}>
      <span className={hideLabel ? "visually-hidden" : undefined}>{label}</span>
      {control}
    </label>
  );
}

/** A select without its own label, for a row whose variable is itself chosen (a component, then its state). */
export function BareSelect<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T | "";
  options: readonly { value: T; label: string }[];
  onChange: (value: T) => void;
}) {
  const missing = value !== "" && !options.some((option) => option.value === value);
  return (
    <select aria-label={label} value={value} onChange={(event) => onChange(event.target.value as T)}>
      {value === "" && <option value="">Choose…</option>}
      {missing && <option value={value}>{value} (not in the design)</option>}
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

/** A checkbox row: the question in the variable column, the box as the value. */
export function CheckRow({
  label,
  checked,
  disabled,
  note,
  onChange,
}: {
  label: string;
  checked: boolean;
  disabled?: boolean;
  /** Why it cannot be ticked, beside it. */
  note?: ReactNode;
  onChange: (checked: boolean) => void;
}) {
  const id = useId();
  return (
    <FieldRow label={label} htmlFor={id} unit={note}>
      <input id={id} type="checkbox" checked={checked} disabled={disabled} onChange={(event) => onChange(event.target.checked)} />
    </FieldRow>
  );
}

/** A text input without its own label, for a row whose variable is itself typed (a fluid's name). */
export function BareTextField({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (value: string) => void; placeholder?: string }) {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  const commit = () => text !== value && onChange(text);
  return (
    <input
      aria-label={label}
      value={text}
      placeholder={placeholder}
      onChange={(event) => setText(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => event.key === "Enter" && commit()}
    />
  );
}

// --- Folding: what is not in use stays out of the way ------------------------------

/**
 * A section behind a caret: its title and, folded, a line of what it holds.
 * Open by default only when it holds something; opened or shut by hand, it
 * stays so (a view preference, kept by `id`).
 */
export function Fold({
  id,
  title,
  summary,
  empty = false,
  children,
}: {
  id: string;
  title: string;
  summary?: ReactNode;
  /** Nothing in it: shut unless this person opened it. */
  empty?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(() => loadFolds()[id] ?? !empty);
  const toggle = () => {
    setOpen(!open);
    saveFold(id, !open);
  };
  return (
    <section className="sx-fold" aria-label={title}>
      <button type="button" className="sx-fold-head" aria-expanded={open} onClick={toggle}>
        <span className="sx-caret" aria-hidden="true">
          {open ? "\u25BE" : "\u25B8"}
        </span>
        <span className="sx-fold-title">{title}</span>
        {summary && <span className="sx-fold-summary">{summary}</span>}
      </button>
      {open && <div className="sx-fold-body">{children}</div>}
    </section>
  );
}

/**
 * One item in a table behind a caret: a row of what it is, and, opened, its
 * fields as the rows beneath. A group of its own, named `label`.
 */
export function FoldRow({
  label,
  summary,
  defaultOpen = false,
  onRemove,
  children,
}: {
  label: string;
  summary: ReactNode;
  defaultOpen?: boolean;
  onRemove?: () => void;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <tbody className="sx-fold-item" aria-label={label}>
      <tr className="sx-fold-row">
        <td colSpan={3}>
          <div className="sx-fold-line">
          <button type="button" className="sx-fold-toggle" aria-expanded={open} aria-label={`${label}: ${open ? "fold" : "unfold"}`} onClick={() => setOpen(!open)}>
            <span className="sx-caret" aria-hidden="true">
              {open ? "\u25BE" : "\u25B8"}
            </span>
            <span className="sx-fold-summary">{summary}</span>
          </button>
          {onRemove && (
            <button type="button" className="sx-icon" aria-label={`Remove ${label.toLowerCase()}`} onClick={onRemove}>
              ×
            </button>
          )}
          </div>
        </td>
      </tr>
      {open && <InTable.Provider value={true}>{children}</InTable.Provider>}
    </tbody>
  );
}

/** A table of `FoldRow`s and plain row groups: variable, value, unit. */
export function SheetTable({ label, children }: { label?: string; children: ReactNode }) {
  return (
    <table className="ws-params sx-fields sx-sheet" aria-label={label}>
      {/* The columns set here, not by a first row that may span them all. */}
      <colgroup>
        <col className="sx-col-name" />
        <col />
        <col className="sx-col-unit" />
      </colgroup>
      <InTable.Provider value={true}>{children}</InTable.Provider>
    </table>
  );
}

/** Plain rows in a `SheetTable`. */
export function SheetRows({ label, children }: { label?: string; children: ReactNode }) {
  return <tbody aria-label={label}>{children}</tbody>;
}

/**
 * "+ add…": one menu of what can be added, grouped; choosing one adds it,
 * and the menu returns to its prompt.
 */
export function AddSelect({
  label,
  options,
  onAdd,
}: {
  label: string;
  options: readonly { value: string; label: string; group?: string }[];
  onAdd: (value: string) => void;
}) {
  const id = useId();
  if (options.length === 0) return null;
  return (
    <div className="sx-add">
      <label htmlFor={id} className="visually-hidden">
        {label}
      </label>
      <select
        id={id}
        value=""
        onChange={(event) => {
          if (event.target.value) onAdd(event.target.value);
        }}
      >
        <option value="">{`+ ${label.toLowerCase()}\u2026`}</option>
        {[...new Set(options.map((option) => option.group))].map((group) => {
          const members = options
            .filter((option) => option.group === group)
            .map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ));
          return group ? (
            <optgroup key={group} label={group}>
              {members}
            </optgroup>
          ) : (
            members
          );
        })}
      </select>
    </div>
  );
}
